import { BACKEND_URL } from '../config/backendUrl';
/* SendCredentialRequestPage — main HyreDocs app version of the
 * Bullhorn embed's "Request Credentials" dialog.
 *
 * Uses the same backend endpoints (`/api/credential-requests/...`) so
 * the recruiter flow is identical regardless of where they trigger it
 * from. Lives at /:tenant/send/credentials and is reachable from the
 * Send Documents hub card.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import PullQualificationsButton from '../components/credentials/PullQualificationsButton';
import { toast } from 'sonner';
import {
  ShieldPlus,
  Send,
  RefreshCw,
  Search,
  ArrowLeft,
  CheckCircle2,
  Copy as CopyIcon,
  User,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { bullhornApi } from '../services/api';

const API_URL = BACKEND_URL || '';

export default function SendCredentialRequestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getTenantPath } = useAuth();
  // Arrives here from the Expiring Credentials dashboard's "Send
  // request"/"Send reminder" button — same flow, just pre-filled instead of
  // starting from a blank Bullhorn search.
  const prefill = location.state?.prefill || null;

  // Recipient
  const [candidateEmail, setCandidateEmail] = useState(prefill?.candidateEmail || '');
  const [candidateName, setCandidateName] = useState(prefill?.candidateName || '');
  const [bullhornCandidateId, setBullhornCandidateId] = useState(prefill?.bullhornCandidateId ? String(prefill.bullhornCandidateId) : '');

  // Bullhorn candidate search — mirrors the pattern recruiters already
  // know from the "Send Document" flow (Choose Candidate step). Search
  // by name, email or Bullhorn id, click a result, the three recipient
  // fields below are auto-filled.
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [candidateSearchResults, setCandidateSearchResults] = useState([]);
  const [candidateSearching, setCandidateSearching] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(prefill ? {
    id: prefill.bullhornCandidateId, name: prefill.candidateName, email: prefill.candidateEmail,
  } : null);

  // Categories
  const [categories, setCategories] = useState([]);
  const [qualificationsLabel, setQualificationsLabel] = useState(null); // this client's name for the Qualifications field
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(prefill?.categoryId ? [prefill.categoryId] : []);
  const [requiredMode, setRequiredMode] = useState('all');
  const [customMessage, setCustomMessage] = useState('');

  // Submit
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setCategoriesLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/credential-requests/bullhorn-categories`, {
          withCredentials: true,
        });
        if (alive) {
          setCategories(r.data?.categories || []);
          setQualificationsLabel(r.data?.qualifications_field?.label || null);
        }
      } catch (e) {
        if (alive) toast.error('Could not load credential categories from Bullhorn');
      } finally {
        if (alive) setCategoriesLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filteredCategories = search
    ? categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : categories;

  // ── Bullhorn candidate search ───────────────────────────────────────
  const searchBullhornCandidates = async () => {
    if (candidateSearchQuery.length < 2) return;
    setCandidateSearching(true);
    try {
      const response = await bullhornApi.searchCandidates(candidateSearchQuery);
      const results = response.data?.candidates || [];
      setCandidateSearchResults(results);
      if (results.length === 0) {
        toast.info(`No candidates matched "${candidateSearchQuery}". Try a name, partial email, or Bullhorn ID — or enter manually below.`);
      }
    } catch (e) {
      toast.error('Failed to search Bullhorn candidates');
    } finally {
      setCandidateSearching(false);
    }
  };

  const selectBullhornCandidate = (c) => {
    setSelectedCandidate(c);
    setCandidateEmail(c.email || '');
    setCandidateName(c.name || '');
    setBullhornCandidateId(String(c.id || ''));
    setCandidateSearchResults([]);
    setCandidateSearchQuery('');
  };

  const clearSelectedCandidate = () => {
    setSelectedCandidate(null);
    setCandidateEmail('');
    setCandidateName('');
    setBullhornCandidateId('');
  };

  const handleSend = async () => {
    if (!candidateEmail) { toast.error('Candidate email is required'); return; }
    if (!bullhornCandidateId) { toast.error('Bullhorn Candidate ID is required — credentials need a candidate to attach to'); return; }
    if (selectedIds.length === 0) { toast.error('Pick at least one credential to request'); return; }

    setSending(true);
    try {
      const items = selectedIds.map(id => {
        const cat = categories.find(c => c.id === id);
        const prefillName = prefill?.categoryId === id ? prefill.categoryName : null;
        return {
          bullhorn_category_id: id,
          bullhorn_category_name: cat?.name || prefillName || `Credential #${id}`,
          required: requiredMode === 'all',
        };
      });

      const r = await axios.post(
        `${API_URL}/api/credential-requests`,
        {
          candidate_email: candidateEmail,
          candidate_name: candidateName || null,
          candidate_bullhorn_id: parseInt(bullhornCandidateId, 10),
          entity_type: 'candidate',
          entity_id: parseInt(bullhornCandidateId, 10),
          items,
          custom_message: customMessage || null,
        },
        { withCredentials: true },
      );
      setResult(r.data);
      toast.success(`Credential request sent — ${items.length} item${items.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send credential request');
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = async (url = result?.short_url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch (e) {
      toast.error('Copy blocked — long-press to copy manually');
    }
  };

  // ── Success state ───────────────────────────────────────────────────
  if (result) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-8 space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold">Credential request sent</h1>
            <p className="text-muted-foreground mt-2">
              {candidateName || candidateEmail} will get an email with a link to upload {result.items?.length} credential{result.items?.length === 1 ? '' : 's'}.
            </p>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {result.requests?.length > 1 ? 'Shareable links — one per credential' : 'Shareable link'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Each credential is its own request with its own link. */}
              {(result.requests?.length ? result.requests : [{ short_url: result.short_url, items: result.items }]).map((r, i) => (
                <div key={r.id || i} className="space-y-1">
                  {result.requests?.length > 1 && (
                    <p className="text-xs font-medium">{r.items?.[0]?.bullhorn_category_name}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <Input value={r.short_url || ''} readOnly className="flex-1 font-mono text-xs" data-testid={i === 0 ? 'credreq-result-link' : `credreq-result-link-${i}`} />
                    <Button variant="outline" onClick={() => handleCopyLink(r.short_url)} data-testid={i === 0 ? 'credreq-copy-link-btn' : `credreq-copy-link-btn-${i}`}>
                      <CopyIcon className="w-4 h-4 mr-1" /> Copy
                    </Button>
                  </div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                Share this link directly via WhatsApp / Teams / SMS if the candidate&apos;s email is unreliable. Link expires in 30 days.
              </p>
            </CardContent>
          </Card>

          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate(getTenantPath('/documents'))} data-testid="credreq-view-docs">
              View in Documents
            </Button>
            <Button
              onClick={() => {
                setResult(null);
                setCandidateEmail('');
                setCandidateName('');
                setBullhornCandidateId('');
                setSelectedIds([]);
                setCustomMessage('');
                setSearch('');
              }}
              data-testid="credreq-send-another-btn"
            >
              <ShieldPlus className="w-4 h-4 mr-2" />
              Send another
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Form state ──────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto py-6 space-y-6" data-testid="send-credential-request-page">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(getTenantPath('/send'))}
            className="mb-2 -ml-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <ShieldPlus className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Request Credentials</h1>
              <p className="text-sm text-muted-foreground">
                Ask a candidate to upload one or more credentials. Uploads write straight to their Bullhorn Credentials tab — no PDF needed.
              </p>
              {prefill && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-2 inline-block" data-testid="credreq-prefill-banner">
                  Pre-filled from Expiring Credentials — check the details below before sending.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recipient */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Recipient</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected candidate pill (after a Bullhorn search) */}
            {selectedCandidate && (
              <div
                className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200"
                data-testid="credreq-selected-candidate"
              >
                <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center border border-emerald-200">
                  <span className="font-heading text-sm font-semibold text-emerald-700">
                    {(selectedCandidate.name || '?').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    <span>{selectedCandidate.name}</span>
                    <span className="text-xs font-mono text-emerald-700 bg-white px-1.5 py-0.5 rounded border border-emerald-200">
                      #{selectedCandidate.id}
                    </span>
                  </p>
                  {selectedCandidate.email && (
                    <p className="text-xs text-muted-foreground">{selectedCandidate.email}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelectedCandidate}
                  data-testid="credreq-clear-candidate-btn"
                >
                  Change
                </Button>
              </div>
            )}

            {/* Bullhorn search — hidden once a candidate has been picked */}
            {!selectedCandidate && (
              <div className="space-y-2" data-testid="credreq-bullhorn-search-block">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Search Bullhorn Candidates</Label>
                <p className="text-xs text-muted-foreground">
                  Search by name, email, or <strong>Candidate ID</strong>.
                </p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={candidateSearchQuery}
                      onChange={(e) => setCandidateSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchBullhornCandidates()}
                      placeholder="Search by name, email, or Candidate ID…"
                      className="pl-10"
                      data-testid="credreq-candidate-search-input"
                    />
                  </div>
                  <Button
                    onClick={searchBullhornCandidates}
                    disabled={candidateSearching || candidateSearchQuery.length < 2}
                    className="btn-secondary"
                    data-testid="credreq-candidate-search-btn"
                  >
                    {candidateSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>

                {candidateSearchResults.length > 0 && (
                  <div className="mt-2 border border-border rounded-lg overflow-hidden" data-testid="credreq-search-results">
                    {candidateSearchResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectBullhornCandidate(c)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors duration-200 text-left"
                        data-testid={`credreq-search-result-${c.id}`}
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-emerald-100">
                          <User className="w-4 h-4 text-emerald-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
                            <span>{c.name}</span>
                            <span className="text-xs font-mono text-muted-foreground bg-slate-100 px-1.5 py-0.5 rounded">
                              #{c.id}
                            </span>
                          </p>
                          {c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-4 my-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">or enter manually</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              </div>
            )}

            {/* Manual recipient fields (always available — selected
                candidate auto-fills these, but recruiter can still edit
                e.g. send to a personal email). */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <Input
                  type="email"
                  value={candidateEmail}
                  onChange={(e) => setCandidateEmail(e.target.value)}
                  placeholder="candidate@example.com"
                  data-testid="credreq-page-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Name (optional)</Label>
                <Input
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  placeholder="Jane Smith"
                  data-testid="credreq-page-name"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Bullhorn Candidate ID *</Label>
              <Input
                type="number"
                value={bullhornCandidateId}
                onChange={(e) => setBullhornCandidateId(e.target.value)}
                placeholder="e.g. 12345"
                data-testid="credreq-page-bhid"
              />
              <p className="text-xs text-muted-foreground">
                Auto-filled when you pick a candidate above. Credentials are attached to this candidate in Bullhorn.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Credentials ({selectedIds.length} selected)
              </CardTitle>
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="credreq-page-clear-btn"
                >
                  Clear
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Required mode */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Items are:</span>
              {[
                { v: 'all', label: 'All required' },
                { v: 'optional', label: 'All optional' },
              ].map(opt => (
                <Button
                  key={opt.v}
                  type="button"
                  size="sm"
                  variant={requiredMode === opt.v ? 'default' : 'outline'}
                  onClick={() => setRequiredMode(opt.v)}
                  data-testid={`credreq-page-required-${opt.v}`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            {/* Pull the candidate's Qualifications from Bullhorn and tick the matching credentials */}
            {/^\d+$/.test(String(bullhornCandidateId || '')) && (
              <PullQualificationsButton
                candidateId={String(bullhornCandidateId)}
                categories={categories}
                selectedIds={selectedIds}
                onSelectIds={setSelectedIds}
                label={qualificationsLabel}
                fetchQualifications={(id) => axios.get(`${API_URL}/api/credential-requests/candidate-qualifications/${id}`, { withCredentials: true }).then((r) => r.data)}
                testId="credreq-page-pull-qualifications"
              />
            )}
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search Bullhorn categories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="credreq-page-search"
              />
            </div>
            {/* List */}
            <div className="border rounded-md max-h-80 overflow-y-auto bg-slate-50">
              {categoriesLoading ? (
                <p className="p-3 text-xs text-muted-foreground flex items-center gap-2">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Loading from Bullhorn…
                </p>
              ) : filteredCategories.length === 0 ? (
                <p className="p-3 text-xs text-muted-foreground">
                  {categories.length === 0
                    ? 'No credential categories available. Check Bullhorn connection.'
                    : 'No matches for that search.'}
                </p>
              ) : (
                filteredCategories.slice(0, 200).map(c => {
                  const checked = selectedIds.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer border-b last:border-b-0 hover:bg-white ${checked ? 'bg-emerald-50' : ''}`}
                      data-testid={`credreq-page-cat-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedIds([...selectedIds, c.id]);
                          else setSelectedIds(selectedIds.filter(id => id !== c.id));
                        }}
                      />
                      <span className="truncate flex-1">{c.name.replace(/^\s+/, '')}</span>
                      <span className="text-xs text-muted-foreground">#{c.id}</span>
                    </label>
                  );
                })
              )}
            </div>
            {filteredCategories.length > 200 && (
              <p className="text-xs text-muted-foreground">
                Showing first 200 results — narrow your search to see more.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Custom message */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Custom message (optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Hi! Please upload these so we can finalise your placement…"
              rows={3}
              data-testid="credreq-page-custom-message"
            />
          </CardContent>
        </Card>

        {/* Send */}
        <div className="flex justify-end gap-2 sticky bottom-4 bg-background/80 backdrop-blur-sm py-2 -mx-2 px-2">
          <Button variant="outline" onClick={() => navigate(getTenantPath('/send'))} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !candidateEmail || !bullhornCandidateId || selectedIds.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="credreq-page-send-btn"
          >
            {sending ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Send Request</>
            )}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
